import { isWeb } from "@memorang/helpers/platform";
import {
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState,
} from "react";
import usePlayer from "../../hooks/usePlayer";
import AudioRecorder, {
	type AudioRecorderLabels,
	type AudioRecorderState,
	type AudioRecorderType,
} from "../AudioRecorder";
import { ConfirmDialog } from "../ConfirmDialog";
import type {
	SessionCaptureError,
	SessionRecorderTrackingHandler,
} from "../StudySession/types";
import useRecorder from "./useRecorder";

export type RecordingLimitAlertProps = {
	title: string;
	body: string;
	confirmCta: string;
};
type Props = {
	recordedAudio: string | undefined;
	handleOnRecordingComplete: (uri: string | undefined) => void;
	disabled?: boolean;
	showDiscardButton?: boolean;
	allowStopWhileRecording?: boolean;
	maxDuration?: number;
	type?: AudioRecorderType;
	labels?: AudioRecorderLabels;
	formatTimerLabel?: (seconds: number) => string;
	recordingLimitAlert?: RecordingLimitAlertProps;
	captureError: SessionCaptureError;
	handleTracking: SessionRecorderTrackingHandler;
	remainingSecondsOverride?: number;
	onLimitReachedChange?: (reached: boolean) => void;
	onRecordingActiveChange?: (active: boolean) => void;
	onPlaybackDurationChange?: (durationMillis: number) => void;
};

export type AudioRecorderContainerRef = {
	start: () => void;
	stop: () => Promise<void>;
	isRecording: boolean;
};

const AudioRecorderContainer = forwardRef<AudioRecorderContainerRef, Props>(
	(
		{
			recordedAudio,
			handleOnRecordingComplete,
			disabled,
			showDiscardButton = true,
			allowStopWhileRecording,
			maxDuration = 45,
			type = "default",
			labels,
			formatTimerLabel,
			recordingLimitAlert,
			captureError,
			handleTracking,
			remainingSecondsOverride,
			onLimitReachedChange,
			onRecordingActiveChange,
			onPlaybackDurationChange,
		},
		ref,
	) => {
		const [limitAlertVisible, setLimitAlertVisible] = useState(false);
		const [hasExpired, setHasExpired] = useState(false);
		const [reachedLimit, setReachedLimit] = useState(false);

		const {
			startRecording,
			stopRecording,
			clearMeterHistory,
			isError,
			isRecording,
			currentTime,
			meterHistory,
			duration,
			unloadRecording,
		} = useRecorder({
			onRecordingComplete: handleOnRecordingComplete,
			captureError,
		});

		// Start a new recording: track, reset expired/limit flags, and record
		const startCapture = useCallback(() => {
			handleTracking("start");
			setHasExpired(false);
			setReachedLimit(false);
			startRecording(0);
		}, [handleTracking, startRecording]);

		useImperativeHandle(
			ref,
			() => ({
				start: startCapture,
				stop: stopRecording,
				isRecording,
			}),
			[startCapture, stopRecording, isRecording],
		);

		const {
			playSound,
			pauseSound,
			isPlaying = false,
			currentTimeInMilliseconds = 0,
			totalTimeInMilliseconds = 0,
			onSeek,
			isEnded,
			unloadSound,
		} = usePlayer(recordedAudio ?? null, isWeb ? duration : undefined);

		const currentMode = isRecording || !recordedAudio ? "recording" : "playing";

		useEffect(() => {
			if (recordedAudio && totalTimeInMilliseconds > 0) {
				onPlaybackDurationChange?.(totalTimeInMilliseconds);
			}
		}, [recordedAudio, totalTimeInMilliseconds, onPlaybackDurationChange]);

		useEffect(() => {
			if (isRecording && currentTime > maxDuration * 1000) {
				stopRecording();
				if (type !== "autoRecord") {
					if (recordingLimitAlert) {
						setLimitAlertVisible(true);
					}
					setReachedLimit(true);
				}
				if (!recordedAudio) {
					setHasExpired(true);
				}
			}
		}, [
			isRecording,
			currentTime,
			maxDuration,
			stopRecording,
			recordedAudio,
			type,
			recordingLimitAlert,
		]);

		useEffect(() => {
			onLimitReachedChange?.(reachedLimit);
		}, [reachedLimit, onLimitReachedChange]);

		useEffect(() => {
			onRecordingActiveChange?.(isRecording);
			return () => {
				if (isRecording) {
					onRecordingActiveChange?.(false);
				}
			};
		}, [isRecording, onRecordingActiveChange]);

		useEffect(() => {
			if (recordedAudio) {
				setHasExpired(false);
			}
		}, [recordedAudio]);

		const handlePlayOrStop = useCallback(async () => {
			if (isEnded && recordedAudio) {
				await onSeek(0);
				playSound();
			} else if (isPlaying) {
				pauseSound();
			} else if (recordedAudio) {
				playSound();
			}
		}, [isEnded, recordedAudio, isPlaying, onSeek, playSound, pauseSound]);

		const handleRecord = useCallback(() => {
			// `disabled` gates capture only — playback stays available.
			if (recordedAudio) {
				handlePlayOrStop();
				return;
			}

			if (disabled) {
				return;
			}

			if (isRecording) {
				handleTracking("end");
				stopRecording();
			} else {
				startCapture();
			}
		}, [
			disabled,
			recordedAudio,
			isRecording,
			handlePlayOrStop,
			handleTracking,
			stopRecording,
			startCapture,
		]);

		const handleDelete = useCallback(async () => {
			pauseSound();
			await new Promise((resolve) => setTimeout(resolve, 500)); // wait for audio to pause properly
			await unloadSound();
			unloadRecording();
			handleOnRecordingComplete(undefined);
			clearMeterHistory();
			setHasExpired(false);
			setReachedLimit(false);
		}, [
			pauseSound,
			unloadSound,
			unloadRecording,
			clearMeterHistory,
			handleOnRecordingComplete,
		]);

		const state: AudioRecorderState = useMemo(() => {
			if (isRecording) {
				return "recording";
			}
			if (recordedAudio) {
				return "recorded";
			}
			if (hasExpired) {
				return "expired";
			}
			return "default";
		}, [isRecording, recordedAudio, hasExpired]);

		const isLoading =
			!recordedAudio && meterHistory.length > 0 && !isError && !isRecording;

		const progress =
			totalTimeInMilliseconds > 0
				? currentTimeInMilliseconds / totalTimeInMilliseconds
				: 0;

		const handleSeek = useCallback(
			async (newProgress: number) => {
				await onSeek(newProgress * totalTimeInMilliseconds);
			},
			[totalTimeInMilliseconds, onSeek],
		);

		const totalTime =
			currentMode === "recording"
				? maxDuration * 1000
				: totalTimeInMilliseconds;

		const currentTimeForDisplay =
			currentMode === "recording" ? currentTime : currentTimeInMilliseconds;

		const internalRemainingSeconds = Math.max(
			0,
			Math.ceil((maxDuration * 1000 - currentTime) / 1000),
		);
		const remainingSeconds =
			remainingSecondsOverride !== undefined
				? remainingSecondsOverride
				: internalRemainingSeconds;

		return (
			<>
				<AudioRecorder
					state={state}
					type={type}
					disabled={disabled}
					showDiscardButton={showDiscardButton}
					allowStopWhileRecording={allowStopWhileRecording}
					handleDelete={handleDelete}
					handleRecord={handleRecord}
					isLoading={isLoading}
					isPlaying={isPlaying}
					meterHistory={meterHistory}
					progress={progress}
					onSeek={handleSeek}
					totalTime={totalTime}
					currentTime={currentTimeForDisplay}
					remainingSeconds={remainingSeconds}
					labels={labels}
					formatTimerLabel={formatTimerLabel}
				/>
				{recordingLimitAlert ? (
					<ConfirmDialog
						title={recordingLimitAlert.title}
						body={recordingLimitAlert.body}
						visible={limitAlertVisible}
						showCloseButton={false}
						dismissCallback={() => setLimitAlertVisible(false)}
						confirmCallback={() => setLimitAlertVisible(false)}
						confirmCta={recordingLimitAlert.confirmCta}
					/>
				) : null}
			</>
		);
	},
);

export default AudioRecorderContainer;
